import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { Clock, CheckCircle, AlertTriangle, ChevronRight } from "lucide-react";
import en from "../../../locales/en";
import cs from "../../../locales/cs";

/**
 * FastTrackCard - Displays a fast track authorization option
 * 
 * Visual treatment per applicability_code:
 * - APPLIES: Green border, green badge, primary CTA
 * - MAYBE_VERIFY: Amber border, amber badge, secondary CTA
 */
export const FastTrackCard = ({
  suggestion,
  onSelect,
  language = "cs",
  disabled = false,
}) => {
  const t = language === "cs" ? cs.checkmate.fastTrack : en.checkmate.fastTrack;
  
  const details = suggestion?.details || {};
  const applicabilityCode = details.applicability_code;
  const subtype = details.fast_track_subtype;
  const authRef = details.authorization_reference || details.authorization_type;
  const reasoning = details.applicability_reasoning || [];
  const timeSaved = details.time_saved_days;
  const validUntil = details.valid_until;
  
  // Determine visual treatment based on applicability code
  const isApplies = applicabilityCode === "APPLIES";
  const isMaybeVerify = applicabilityCode === "MAYBE_VERIFY";
  
  const borderClass = isApplies
    ? "border-2 border-emerald-500 dark:border-emerald-600"
    : "border-2 border-amber-500 dark:border-amber-600";
  
  const badgeClass = isApplies
    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
    : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
  
  const badgeText = isApplies ? t.appliesBadge : t.verifyBadge;
  const ctaText = isApplies ? t.useThisPath : t.verifyAndAuthorize;
  
  // Get subtype label from i18n
  const subtypeLabel = t.subtypes[subtype] || subtype;
  
  // Format valid until date
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString(language === "cs" ? "cs-CZ" : "en-US", {
        day: "numeric",
        month: "short",
        year: "numeric"
      });
    } catch {
      return dateStr;
    }
  };
  
  const handleClick = () => {
    if (!disabled && onSelect) {
      onSelect(suggestion);
    }
  };

  return (
    <Card 
      className={`${borderClass} transition-all ${disabled ? "opacity-60" : "hover:shadow-md cursor-pointer"}`}
      onClick={disabled ? undefined : handleClick}
      data-testid={`fast-track-card-${details.authorization_id}`}
    >
      <CardContent className="p-4">
        {/* Top row: Subtype label + applies/verify badge + time saved */}
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
              {subtypeLabel}
            </span>
            <Badge className={`${badgeClass} text-xs`}>
              {isApplies ? (
                <CheckCircle className="h-3 w-3 mr-1" />
              ) : (
                <AlertTriangle className="h-3 w-3 mr-1" />
              )}
              {badgeText}
            </Badge>
          </div>
          
          {timeSaved && (
            <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700">
              <Clock className="h-3 w-3 mr-1" />
              {t.timeSaved.replace("{days}", timeSaved)}
            </Badge>
          )}
        </div>
        
        {/* Title: Authorization reference */}
        <h4 className="text-base font-semibold text-[var(--text-primary)] mb-2">
          {authRef}
        </h4>
        
        {/* Body: Reasoning bullets (max 4) */}
        {reasoning.length > 0 && (
          <div className="space-y-1 mb-3">
            {reasoning.slice(0, 4).map((reason, idx) => (
              <p key={idx} className="text-xs text-[var(--text-secondary)] flex items-start gap-1.5">
                <span className="mt-0.5">{reason.startsWith("✓") || reason.startsWith("✗") || reason.startsWith("⚠") ? "" : "•"}</span>
                <span>{reason}</span>
              </p>
            ))}
            {reasoning.length > 4 && (
              <p className="text-xs text-[var(--text-muted)] italic">
                +{reasoning.length - 4} more...
              </p>
            )}
          </div>
        )}
        
        {/* Footer: Valid until date + CTA button */}
        <div className="flex items-center justify-between pt-3 border-t border-[var(--border-default)]">
          <div className="text-xs text-[var(--text-muted)]">
            {validUntil && (
              <span>{t.validUntil.replace("{date}", formatDate(validUntil))}</span>
            )}
          </div>
          
          <Button
            size="sm"
            variant={isApplies ? "default" : "outline"}
            className={`gap-1.5 ${isApplies ? "bg-emerald-600 hover:bg-emerald-700" : "border-amber-500 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"}`}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            data-testid={`fast-track-cta-${details.authorization_id}`}
          >
            {ctaText}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default FastTrackCard;
